import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichText from "@/components/RichText";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const CATEGORIES = ["founder", "about", "service_description", "next_steps", "terms", "pricing_explanation", "catchup", "contact", "general"];

export default function LibraryTab() {
  const [library, setLibrary] = useState([]);
  const [nl, setNl] = useState({ category: "founder", name: "", html: "" });
  const [editLib, setEditLib] = useState(null);

  useEffect(() => { api.get("/content-library").then((r) => setLibrary(r.data)).catch(() => {}); }, []);

  const createLib = async () => {
    if (!nl.name) return toast.error("Snippet name required");
    const { data } = await api.post("/content-library", { ...nl, kind: "text" });
    setLibrary([data, ...library]);
    setNl({ category: "founder", name: "", html: "" });
    toast.success("Snippet added");
  };
  const delLib = async (lid) => { await api.delete(`/content-library/${lid}`); setLibrary(library.filter((x) => x.id !== lid)); };
  const saveLib = async () => {
    if (!editLib.name.trim()) return toast.error("Snippet name required");
    const { data } = await api.put(`/content-library/${editLib.id}`, { category: editLib.category, name: editLib.name, html: editLib.html });
    setLibrary(library.map((x) => (x.id === editLib.id ? data : x)));
    setEditLib(null); toast.success("Snippet updated");
  };

  return (
    <div className="mt-4 space-y-4 max-w-3xl">
      <Card className="p-4 rounded-md space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <Select value={nl.category} onValueChange={(v) => setNl({ ...nl, category: v })}>
            <SelectTrigger data-testid="lib-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input className="col-span-2" placeholder="Snippet name" value={nl.name} onChange={(e) => setNl({ ...nl, name: e.target.value })} data-testid="lib-name" />
        </div>
        <RichText value={nl.html} onChange={(html) => setNl({ ...nl, html })} testId="lib-html" />
        <Button onClick={createLib} className="bg-cass-navy text-white" data-testid="add-lib-btn"><Plus className="w-4 h-4 mr-1" />Add snippet</Button>
      </Card>
      {library.map((x) => (
        <Card key={x.id} className="p-4 rounded-md flex items-start justify-between" data-testid={`lib-${x.id}`}>
          <div className="min-w-0"><Badge variant="secondary">{x.category}</Badge><div className="font-medium text-cass-navy mt-1">{x.name}</div><div className="text-sm text-cass-slate mt-1 line-clamp-2 doc-prose" dangerouslySetInnerHTML={{ __html: x.html }} /></div>
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="sm" onClick={() => setEditLib({ ...x })} data-testid={`edit-lib-${x.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => delLib(x.id)} data-testid={`del-lib-${x.id}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        </Card>
      ))}
      <Dialog open={!!editLib} onOpenChange={(o) => !o && setEditLib(null)}>
        <DialogContent className="max-w-xl" data-testid="edit-lib-dialog">
          <DialogHeader><DialogTitle>Edit snippet</DialogTitle><DialogDescription>Update this content library snippet.</DialogDescription></DialogHeader>
          {editLib && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <Select value={editLib.category} onValueChange={(v) => setEditLib({ ...editLib, category: v })}>
                  <SelectTrigger data-testid="edit-lib-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input className="col-span-2" placeholder="Snippet name" value={editLib.name} onChange={(e) => setEditLib({ ...editLib, name: e.target.value })} data-testid="edit-lib-name" />
              </div>
              <RichText value={editLib.html} onChange={(html) => setEditLib({ ...editLib, html })} testId="edit-lib-html" />
            </div>
          )}
          <DialogFooter><Button onClick={saveLib} className="bg-cass-navy text-white" data-testid="save-lib-btn">Save snippet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
